import { apiClient } from './apiClient';

export interface CompanyChatRecipientRow {
  userId: string;
  username?: string;
  email?: string;
  fullName?: string;
}

interface UsersApiRow {
  id: string;
  username?: string;
  email?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  is_active?: boolean;
}

interface CompanyInstanceApiRow {
  user_id: string;
  username?: string;
  email?: string;
  full_name?: string;
  is_active?: boolean;
}

function displayNameFromUser(row: UsersApiRow): string {
  if (row.full_name?.trim()) return row.full_name.trim();
  const parts = [row.first_name, row.last_name].filter(Boolean);
  if (parts.length) return parts.join(' ');
  return row.username?.trim() || row.email?.trim() || row.id;
}

function mapUsersRows(rows: UsersApiRow[]): CompanyChatRecipientRow[] {
  return rows
    .filter((row) => row.is_active !== false)
    .map((row) => ({
      userId: row.id,
      username: row.username,
      email: row.email,
      fullName: displayNameFromUser(row),
    }));
}

function mapInstanceRows(rows: CompanyInstanceApiRow[]): CompanyChatRecipientRow[] {
  return rows
    .filter((row) => row.is_active !== false && row.user_id)
    .map((row) => ({
      userId: row.user_id,
      username: row.username,
      email: row.email,
      fullName: row.full_name?.trim() || row.username?.trim() || row.email?.trim() || row.user_id,
    }));
}

/**
 * Active users in the selected company (roster / company_instances), not global user list.
 * Authenticated `/users?company_id=` first; `/company-instances?company_id=` if that fails.
 */
export async function fetchCompanyChatRecipients(companyId: string): Promise<CompanyChatRecipientRow[]> {
  try {
    const response = await apiClient.get<UsersApiRow[]>('/users', {
      params: { company_id: companyId, is_active: true, limit: 200 },
    });
    return mapUsersRows(response.data ?? []);
  } catch (primaryError) {
    try {
      const fallback = await apiClient.get<CompanyInstanceApiRow[]>('/company-instances', {
        params: { company_id: companyId, active_only: true, limit: 200 },
      });
      return mapInstanceRows(fallback.data ?? []);
    } catch {
      throw primaryError;
    }
  }
}
